import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { AgentEditorPresence } from './presenceTypes';

const MAX_VISIBLE = 3;

const AVATAR_PALETTE = [
    'bg-blue-500',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-amber-500',
    'bg-cyan-500',
    'bg-rose-500',
    'bg-indigo-500',
    'bg-teal-500',
    'bg-orange-500',
];

function hashString(input: string): number {
    let hash = 0;
    for (let i = 0; i < input.length; i++) {
        hash = (hash << 5) - hash + input.charCodeAt(i);
        hash |= 0;
    }
    return Math.abs(hash);
}

function colorForEditor(editor: AgentEditorPresence): string {
    const seed =
        editor.id ||
        editor.email ||
        editor.connection_id ||
        editor.name ||
        'unknown';
    return AVATAR_PALETTE[hashString(seed) % AVATAR_PALETTE.length];
}

function initialsForEditor(editor: AgentEditorPresence): string {
    const name = (editor.name || '').trim();
    if (name) {
        const parts = name.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        if (parts.length === 1) {
            return parts[0].slice(0, 2).toUpperCase();
        }
    }
    const email = (editor.email || '').trim();
    if (email) {
        return email[0].toUpperCase();
    }
    return '?';
}

function presenceLabel(editor: AgentEditorPresence): string {
    if (editor.status === 'editing') return 'Editing now';
    return 'Active now';
}

function editorKey(editor: AgentEditorPresence, fallbackIndex: number): string {
    return (
        editor.connection_id ||
        editor.id ||
        editor.email ||
        `editor-${fallbackIndex}`
    );
}

interface EditorAvatarProps {
    editor: AgentEditorPresence;
    className?: string;
    children?: React.ReactNode;
}

function EditorAvatar({ editor, className, children }: EditorAvatarProps) {
    return (
        <div
            className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold uppercase text-white ring-2 ring-background ${colorForEditor(
                editor
            )} ${className ?? ''}`}
        >
            {children ?? initialsForEditor(editor)}
        </div>
    );
}

interface EditorPresenceStackProps {
    editors: AgentEditorPresence[];
}

export default function EditorPresenceStack({
    editors,
}: EditorPresenceStackProps) {
    if (!editors.length) return null;

    const visible = editors.slice(0, MAX_VISIBLE);
    const overflow = editors.length - visible.length;

    return (
        <TooltipProvider delayDuration={150}>
            <div className="flex items-center">
                {visible.map((editor, index) => (
                    <Tooltip key={editorKey(editor, index)}>
                        <TooltipTrigger asChild>
                            <div className={index > 0 ? '-ml-2' : ''}>
                                <EditorAvatar editor={editor} />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            <div className="flex flex-col gap-0.5">
                                <div className="font-medium">
                                    {editor.name || 'Someone'}
                                </div>
                                {editor.email && (
                                    <div className="text-[11px] italic opacity-80">
                                        {editor.email}
                                    </div>
                                )}
                                <div className="text-[11px] opacity-80">
                                    {presenceLabel(editor)}
                                </div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                ))}
                {overflow > 0 && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div className="-ml-2">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground ring-2 ring-background">
                                    +{overflow}
                                </div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                            <div className="flex flex-col gap-0.5">
                                {editors.slice(MAX_VISIBLE).map((editor, index) => (
                                    <div
                                        key={editorKey(editor, MAX_VISIBLE + index)}
                                        className="text-[11px]"
                                    >
                                        {editor.name || editor.email || 'Someone'}
                                    </div>
                                ))}
                            </div>
                        </TooltipContent>
                    </Tooltip>
                )}
            </div>
        </TooltipProvider>
    );
}
